import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { useMonitoringEventGroups } from "../../api";
import { Ago, EmptyBlock, FetchingDot, LevelBadge, SectionCard, StatePill } from "../../components/primitives";
import { formatCount, formatStamp, useNow } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import type { EventGroup, EventLevel } from "../../types";
import { QueryBody } from "../requests/bits";
import { LevelToggle, SourceCode } from "./bits";
import { DEFAULT_LEVELS, ONGOING_MS } from "./utils";

const PAGE = 10;

/**
 * The problems of the board window grouped by fingerprint (source.code@subject), the most frequent first.
 * A click narrows the event log below to the group.
 */
export function EventGroups({ onSelect, memory }: { onSelect: (group: EventGroup) => void; memory: boolean }) {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const [levels, setLevels] = useState<EventLevel[]>(DEFAULT_LEVELS);
  const [showAll, setShowAll] = useState(false);
  const groups = useMonitoringEventGroups(window, levels);

  return (
    <SectionCard
      title={t("monitoring.events.groups.title")}
      description={t("monitoring.events.groups.description", { window: t(`monitoring.window.long.${window}`) })}
      actions={
        <>
          <FetchingDot active={groups.isFetching} />
          <LevelToggle value={levels} onChange={setLevels} label={t("monitoring.events.groups.levels")} />
        </>
      }
    >
      <QueryBody query={groups} height={200}>
        {(data) => {
          if (!data.groups.length) {
            return (
              <EmptyBlock>{memory ? t("monitoring.events.groups.memory") : t("monitoring.events.groups.empty")}</EmptyBlock>
            );
          }
          const shown = showAll ? data.groups : data.groups.slice(0, PAGE);
          return (
            <>
              <ul className="flex flex-col divide-y divide-border/60">
                {shown.map((group) => (
                  <li key={group.fingerprint}>
                    <GroupRow group={group} onSelect={onSelect} />
                  </li>
                ))}
              </ul>
              {data.groups.length > PAGE && (
                <div className="mt-2 flex justify-center">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
                    {showAll
                      ? t("monitoring.events.groups.showFewer", { count: PAGE })
                      : t("monitoring.events.groups.showAll", { count: data.groups.length })}
                  </Button>
                </div>
              )}
            </>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}

function GroupRow({ group, onSelect }: { group: EventGroup; onSelect: (group: EventGroup) => void }) {
  const { t, tp } = useLocalization();
  const now = useNow();
  const ongoing = group.level !== "info" && !!group.last && now - group.last < ONGOING_MS;
  const folded = group.count - group.events;
  return (
    <button
      type="button"
      onClick={() => onSelect(group)}
      title={t("monitoring.events.groups.showInLog")}
      className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <LevelBadge level={group.level} />
          <SourceCode source={group.source} code={group.code} />
          {ongoing && (
            <StatePill tone={group.level === "error" ? "nok" : "warn"} pulse className="h-5 px-1.5 text-[10px]">
              {t("monitoring.events.groups.ongoing")}
            </StatePill>
          )}
        </div>
        {group.subject && <div className="mt-1 break-all font-mono text-[11px] text-foreground/80">{group.subject}</div>}
        <div className="mt-1 line-clamp-2 break-words text-xs text-muted-foreground">{group.message}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span title={formatStamp(group.first)}>
            {t("monitoring.events.groups.first")} <Ago ts={group.first} />
          </span>
          <span title={formatStamp(group.last)}>
            {t("monitoring.events.groups.last")} <Ago ts={group.last} />
          </span>
          {group.pizzas > 0 && <span>{t("monitoring.events.groups.pizzas", { pizzas: tp("count.pizzas", group.pizzas) })}</span>}
          {folded > 0 && (
            <span title={t("monitoring.events.foldedHint")}>
              {t("monitoring.events.groups.stored", { events: formatCount(group.events), repeats: formatCount(folded) })}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <div className="text-right">
          <div className="font-mono text-base font-semibold tabular-nums">×{formatCount(group.count)}</div>
          <div className="text-[10px] text-muted-foreground">{t("monitoring.events.groups.occurrences")}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
      <span className="sr-only">{t("monitoring.events.groups.showInLog")}</span>
    </button>
  );
}
